import { Box, Typography } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import { HStack } from "Components/_Shared/Core/Stack";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { getCzechPluralForm } from "Infrastructure/Utils/LanguageUtils";
import type { FC } from "react";

const HEIGHT = 28;
const DOT_SIZE = 8;

const pulse = keyframes`
  from { transform: scale(1); opacity: 0.6; }
  to { transform: scale(2.5); opacity: 0; }
`;

type Props = {
  readonly count: number;
};

export const FormDrawerUnsavedBadge: FC<Props> = ({ count }) => {
  const { t } = useResource();

  return (
    <HStack
      spacing={Spacing.M}
      alignItems="center"
      flexShrink={0}
      height={HEIGHT}
      px={Spacing.XL}
      bgcolor="primary.light"
      border={1}
      borderRadius={Radius.MAX}
      sx={(theme) => ({ borderColor: theme.colors.brandLightBorder })}
    >
      <Box
        position="relative"
        width={DOT_SIZE}
        height={DOT_SIZE}
        flexShrink={0}
        bgcolor="primary.main"
        borderRadius={Radius.MAX}
        sx={{
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            bgcolor: "inherit",
            animation: `${pulse} 1.6s ease-out infinite`,
          },
          "@media (prefers-reduced-motion: reduce)": { "&::after": { display: "none" } },
        }}
      />
      <Typography fontSize={13} fontWeight={500} color="text.primary" noWrap>
        <Box component="strong" fontWeight={700}>
          {count}
        </Box>{" "}
        {t(
          getCzechPluralForm(count, {
            one: Resources.Common.UnsavedChanges_one,
            few: Resources.Common.UnsavedChanges_few,
            other: Resources.Common.UnsavedChanges_other,
          }),
        )}
      </Typography>
    </HStack>
  );
};
